import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useCreateCustomerSessionQuery } from "src/api/services/paymentsApi";
import { useGetVisitQuery } from "src/api/services/visitApi";
import { Coupon, ProductPrice } from "src/api/types";
import Spinner from "src/components/Loading/Spinner";
import {
  PaymentFormWithoutVisit,
  PaymentFormWithVisit,
} from "src/components/PaymentForm/PaymentForm";
import { MatchParam } from "src/constants/matches";
import PaymentProvider from "src/context/PaymentProvider";
import { useProduct } from "src/context/ProductProvider";
import StripeContextProvider from "src/context/StripeContext";

interface Props {
  quantity?: number;
  product: ProductPrice;
  coupon: Coupon | null;
  button?: string;
  dueToday: number;
}

const PaymentProcessor = ({ product, coupon, button, dueToday }: Props) => {
  const billingModel = useProduct((product) => product.billing_model);
  const { t } = useTranslation();
  const { visitId } = useParams<MatchParam<"VISIT_ID">>();
  const { data: visit, isLoading } = useGetVisitQuery(visitId ?? skipToken);
  const { data } = useCreateCustomerSessionQuery();
  const page = "payment-information";

  const buttonText = useMemo(() => {
    if (button != null) {
      return button;
    }

    return t(`${page}.${billingModel}.submit`, "I'm Ready to Begin");
  }, [billingModel, button, t]);

  if (isLoading) {
    return <Spinner />;
  }

  if (visitId != null && visit == null) {
    throw new Error("Visit not found");
  }

  return (
    <StripeContextProvider
      billingModel={billingModel}
      amount={dueToday}
      customerSessionToken={data?.token}
    >
      <PaymentProvider product={product} coupon={coupon}>
        {visitId != null && visit != null ? (
          <PaymentFormWithVisit
            visit={visit}
            buttonText={buttonText}
            product={product}
            coupon={coupon}
            dueToday={dueToday}
          />
        ) : (
          <PaymentFormWithoutVisit
            buttonText={buttonText}
            product={product}
            coupon={coupon}
            dueToday={dueToday}
          />
        )}
      </PaymentProvider>
    </StripeContextProvider>
  );
};

export default PaymentProcessor;
